import { useCallback, useEffect, useState } from 'react';

const FLAG_KEY = 'newUIEnabled';
const PROMPT_KEY = 'newUIPromptShown';
const THEME_KEY = 'newUITheme';

// The new UI is currently released to this allow-list only. Everyone else is
// locked to the classic UI regardless of their stored flag, and never sees the
// "Experience New Layout" switch. Keep entries lowercase.
export const NEW_UI_ALLOWED_EMAILS = ['layne@projector.app', 'jia6@yopmail.com'];

// Is a given email allowed to use the new UI?
export const canUseNewUI = (email) => NEW_UI_ALLOWED_EMAILS
  .includes((email || '').trim().toLowerCase());

// Reads the email persisted by getMyProfile (app.jsx) so the gate works
// synchronously in the non-reactive isNewUIEnabled() call.
const emailAllowed = () => {
  try {
    return canUseNewUI(localStorage.getItem('userEmail'));
  } catch {
    return false;
  }
};

// Classic (production) UI is the default. An allow-listed user can opt into the
// new UI from Settings — stored as newUIEnabled='true'. Anything else (not
// allow-listed, missing key, storage blocked) resolves to classic.
const readFlag = () => {
  try {
    if (!emailAllowed()) return false;
    return localStorage.getItem(FLAG_KEY) === 'true';
  } catch {
    return false;
  }
};

const readPromptShown = () => {
  try {
    return localStorage.getItem(PROMPT_KEY) === 'true';
  } catch {
    return true;
  }
};

const readTheme = () => {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

const emit = () => {
  try {
    window.dispatchEvent(new Event('newUIFlagChange'));
  } catch {
    /* noop */
  }
};

export const isNewUIEnabled = () => readFlag();

export const setNewUIEnabled = (val) => {
  try {
    localStorage.setItem(FLAG_KEY, val ? 'true' : 'false');
    localStorage.setItem(PROMPT_KEY, 'true');
    emit();
  } catch {
    /* noop */
  }
};

export const markPromptShown = () => {
  try {
    localStorage.setItem(PROMPT_KEY, 'true');
    emit();
  } catch {
    /* noop */
  }
};

export const setNewUITheme = (mode) => {
  try {
    localStorage.setItem(THEME_KEY, mode === 'dark' ? 'dark' : 'light');
    emit();
  } catch {
    /* noop */
  }
};

export const useNewUI = () => {
  const [enabled, setEnabled] = useState(readFlag());
  const [promptShown, setPromptShownState] = useState(readPromptShown());
  const [themeMode, setThemeModeState] = useState(readTheme());

  useEffect(() => {
    const sync = () => {
      setEnabled(readFlag());
      setPromptShownState(readPromptShown());
      setThemeModeState(readTheme());
    };
    window.addEventListener('newUIFlagChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('newUIFlagChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const enable = useCallback(() => setNewUIEnabled(true), []);
  const disable = useCallback(() => setNewUIEnabled(false), []);
  const dismissPrompt = useCallback(() => markPromptShown(), []);
  const toggleTheme = useCallback(() => {
    setNewUITheme(readTheme() === 'dark' ? 'light' : 'dark');
  }, []);
  const setTheme = useCallback((mode) => setNewUITheme(mode), []);

  return {
    enabled,
    promptShown,
    themeMode,
    enable,
    disable,
    dismissPrompt,
    toggleTheme,
    setTheme,
  };
};
